import { brandingVariables } from 'smartsuite-ui';
import { colors } from '../../../../shared/constants/constants';
import { getFormattedValue } from '../../../dashboardUtils';
import { getFormattedTimeFromMilliseconds } from '../../utils/dataFormatUtils';

const gaugeDialUtils = (
    configuration,
    value,
    { badThreshold = {}, okThreshold = {}, goodThreshold = {} }
) => ({
    badToGood: true,
    endAngle: -270,
    fillColors: {},
    labelAboveCenterPercentage: 0,
    showRemainingShadow: true,
    showThresholdLabel: false,
    showThresholds: true,
    startAngle: 90,
    thresholdValues: {},

    // eslint-disable-next-line complexity
    initialize() {
        this.badToGood = configuration.badToGood ?? this.badToGood;
        this.endAngle = configuration.endAngle ?? this.endAngle;
        this.labelAboveCenterPercentage =
            configuration.labelAboveCenterPercentage ??
            this.labelAboveCenterPercentage;
        this.showRemainingShadow =
            configuration.showRemainingShadow ?? this.showRemainingShadow;
        this.showThresholdLabel =
            configuration.showThresholdLabel ?? this.showThresholdLabel;
        this.showThresholds =
            configuration.showThresholds ?? this.showThresholds;
        this.startAngle = configuration.startAngle ?? this.startAngle;

        this.fillColors = {
            default: colors[0],
            first: this.badToGood
                ? brandingVariables.error
                : brandingVariables.success,
            middle: brandingVariables.warning,
            full: this.badToGood
                ? brandingVariables.success
                : brandingVariables.error,
            remaining: this.showRemainingShadow
                ? brandingVariables.backing
                : brandingVariables.accent0,
        };

        this.thresholdValues = {
            first: this.badToGood ? badThreshold.value : goodThreshold.value,
            middle: okThreshold.value,
            full: this.badToGood ? goodThreshold.value : badThreshold.value,
        };
    },

    // eslint-disable-next-line complexity
    getFillBasedOnThresholds() {
        const valueAsFloat = parseFloat(value);

        if (!this.thresholdValues.first || !this.thresholdValues.middle) {
            return this.fillColors.default;
        }

        if (valueAsFloat < this.thresholdValues.first) {
            return this.fillColors.first;
        }

        if (valueAsFloat < this.thresholdValues.middle) {
            return this.fillColors.middle;
        }

        return this.fillColors.full;
    },

    getThresholdCells() {
        if (!this.showThresholds) {
            return [];
        }

        const thresholdCells = [];
        thresholdCells.push({
            value:
                (this.thresholdValues.first / this.thresholdValues.full) * 100,
            fill: this.fillColors.first,
            fillOpacity: 0.5,
        });

        thresholdCells.push({
            value:
                (this.thresholdValues.middle / this.thresholdValues.full) *
                    100 -
                thresholdCells[0].value,
            fill: this.fillColors.middle,
            fillOpacity: 0.5,
        });

        thresholdCells.push({
            value:
                this.thresholdValues.full -
                thresholdCells[1].value -
                thresholdCells[0].value,
            fill: this.fillColors.full,
            fillOpacity: 0.5,
        });

        return thresholdCells;
    },

    getValueCells() {
        const restrictedValue = Math.max(
            0,
            Math.min(parseFloat(value), this.thresholdValues.full)
        );

        return [
            {
                name: 'data',
                value: restrictedValue,
                fill: this.getFillBasedOnThresholds(),
            },
            {
                name: 'whitespace',
                value: this.thresholdValues.full - restrictedValue,
                fill: this.fillColors.remaining,
            },
        ];
    },

    getValueLabel() {
        const formatter = configuration.isTime
            ? getFormattedTimeFromMilliseconds
            : undefined;
        const valueString = getFormattedValue(
            value,
            configuration.suffix,
            formatter,
            configuration.timeFormat
        );
        const thresholdString = getFormattedValue(
            this.thresholdValues.full,
            configuration.suffix,
            formatter,
            configuration.timeFormat
        );

        if (!this.showThresholdLabel) {
            return valueString;
        }

        return `${valueString}/${thresholdString}`;
    },
});

export default gaugeDialUtils;
